<template>
    <div class="flex-grow flex flex-col justify-center">
        <div class="relative" style="width: 100%; height: 450px">
            <canvas id="credit-score-history-chart"></canvas>
        </div>
        <div id="credit-score-history-chart-legend" class="px-5 pt-2 pb-6">
            <ul class="flex flex-wrap justify-center -m-1"></ul>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCreditScoreStore } from '@/store/creditscore';
import {
    Chart,
    CategoryScale,
    LineController,
    LineElement,
    Filler,
    PointElement,
    LinearScale,
    TimeScale,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(
    CategoryScale,
    LineController,
    LineElement,
    Filler,
    PointElement,
    LinearScale,
    TimeScale,
    Tooltip,
    ChartDataLabels
);

let chart = false;

async function getTooltipEl(chart) {
    let tooltipEl = chart.canvas.parentNode.querySelector('div');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.style.background = '#ffffff';
        tooltipEl.style.border = '1px solid #ebf0f4';
        tooltipEl.style.borderRadius = '3px';
        tooltipEl.style.color = 'black';
        tooltipEl.style.opacity = 1;
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        chart.canvas.parentNode.appendChild(tooltipEl);
    }
    return new Promise((resolve) => {
        resolve(tooltipEl);
    });
}

const externalTooltip = (context) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    getTooltipEl(chart).then((tooltipEl) => {
        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        // Set Text
        if (tooltip.body) {
            const bodyLines = tooltip.body.map((b) => b.lines);
            const tableHead = document.createElement('thead');
            const tableBody = document.createElement('tbody');

            bodyLines.forEach((body, i) => {
                const colors = tooltip.labelColors[i];

                const span = document.createElement('span');
                span.style.background = colors.backgroundColor;
                span.style.borderColor = colors.borderColor;
                span.style.pointerEvents = 'none';
                span.style.borderWidth = '2px';
                span.style.marginRight = '10px';
                span.style.height = '10px';
                span.style.width = '10px';
                span.style.display = 'inline-block';

                const tr = document.createElement('tr');
                tr.style.backgroundColor = 'inherit';
                tr.style.borderWidth = 0;

                const td = document.createElement('td');
                td.style.borderWidth = 0;

                const text = document.createTextNode(body);

                td.appendChild(span);
                td.appendChild(text);
                tr.appendChild(td);
                tableBody.appendChild(tr);
            });

            const tableRoot = tooltipEl.querySelector('table');

            // Remove old children
            while (tableRoot.firstChild) {
                tableRoot.firstChild.remove();
            }

            // Add new children
            tableRoot.appendChild(tableHead);
            tableRoot.appendChild(tableBody);
        }

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

        // Display, position, and set styles for font
        tooltipEl.style.opacity = 1;
        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
        tooltipEl.style.top = positionY + tooltip.caretY + 'px';
        tooltipEl.style.font = tooltip.options.bodyFont.string;
        tooltipEl.style.padding =
            tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
    });
};

export default {
    props: {},
    components: {},
    data: () => ({
        configLine: {
            type: 'line',
            data: {
                labels: [],
                datasets: [],
            },
            options: {
                responsive: true,
                scales: {
                    x: {
                        gridLines: {
                            display: false,
                        },
                        ticks: {
                            padding: 25,
                        },
                    },
                    y: {
                        ticks: {
                            stepSize: 10,
                            beginAtZero: true,
                            callback: (yValue) => {
                                return Math.floor(yValue);
                            },
                        },
                    },
                },
                plugins: {
                    datalabels: {
                        color: '#000',
                        align: 'bottom',
                    },
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        //events: ['click', 'mouseout'],
                        enabled: false,
                        position: 'nearest',
                        external: externalTooltip,
                    },
                    title: {
                        display: false,
                    },
                },
            },
            onAnimationComplete: function () {
                var ctx = this.chart.ctx;
                ctx.font = this.scale.font;
                ctx.fillStyle = this.scale.textColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';

                this.datasets.forEach(function (dataset) {
                    dataset.bars.forEach(function (bar) {
                        ctx.fillText(bar.value, bar.x, bar.y - 5);
                    });
                });
            },
        },
    }),
    directives: {},
    computed: {
        ...mapState(useCreditScoreStore, [
            'loadingUserCreditScore',
            'historyLineChart',
        ]),
    },
    watch: {
        loadingUserCreditScore(newVal, oldVal) {
            this.chartDataLoaded = false;
            console.log('loading');
            if (!newVal && oldVal) {
                this.getHistoryLineChart()
                    .then(() => {
                        this.updateChartsData();
                        this.chartDataLoaded = true;
                    })
                    .catch(() => {
                        // suppress
                    });
            }
        },
    },
    methods: {
        ...mapActions(useCreditScoreStore, ['getHistoryLineChart']),
        initCharts() {
            const ctx = document.getElementById('credit-score-history-chart');
            chart = new Chart(ctx, this.configLine);
        },
        updateChartsData() {
            this.setLabels();
            this.setDataset();
            chart.update();
        },
        setLabels() {
            this.configLine.data.labels = this.historyLineChart.labels;
        },
        setDataset() {
            this.configLine.data.datasets = [
                {
                    data: this.historyLineChart.datasets,
                    pointBorderColor: this.historyLineChart.colors,
                    pointStyle: 'circle',
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    borderWidth: 3,
                    fillColor: 'transparent',
                },
            ];
        },
    },
    created() {},
    mounted() {
        this.initCharts();
        this.updateChartsData();
    },
};
</script>
